import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { motion } from "framer-motion";

import * as Common from "../commons/common";
import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as Utils from "../utils/utils";

import PageHeader, { TypeHeader } from "../components/PageHeader";
import { useAsyncAxios, utilAxiosWithAppToken, utilAxiosWithAuth } from "../utils/customAxios";
import { useAuthDispatch } from "../provider/AuthProvider";

import styles from "./PageLayout.module.css";
import PageFooter from "../components/PageFooter";

declare const window: typeof globalThis & {
    Android: any;
};

const pageVariants = {
    squish: {
        opacity: 0,
        x: "-10vw",
    },
    appear: {
        opacity: 1,
        x: "0",
        transition: { duration: 0.15 },
    },

    exit: {
        opacity: 0,
        transition: { ease: "easeIn", duration: 0.15 },
    },
};

function PageLayout({
    children,
    headerType,
    showFooter,
    overflow,
}: {
    children: React.ReactNode;
    headerType?: TypeHeader;
    showFooter?: boolean;
    overflow?: boolean;
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAuthDispatch();
    const params = useParams();

    const [currentPaymentCategoryId, setCurrentPaymentCategoryId] = useState<number>();
    const [paymentCategory, setPaymentCategory] = useState<Types.RentPaymentCategoryDto>();
    const [manualContent, setManualContent] = useState<string>();
    const [currentHeaderType, setCurrentHeaderType] = useState<TypeHeader>();

    useEffect(() => {
        if (params.categoryId) setCurrentPaymentCategoryId(Number(params.categoryId));
        if (params.manualContent) {
            switch (params.manualContent) {
                case "password":
                    setManualContent("사무실 디지털 락 비밀번호 ");
                    break;
                case "car":
                    setManualContent("사무실 및 차량 관리");
                    break;
                case "return":
                    setManualContent("출/반납 매뉴얼");
                    break;
                case "faq":
                    setManualContent("자주하는 질문");
                    break;
                case "user":
                    setManualContent("캠핑카 사용 매뉴얼");
                    break;
                case "emergency":
                    setManualContent("긴급 상황 대처 매뉴얼");
                    break;
                case "penalty":
                    setManualContent("위약금");
                    break;
                default:
                    break;
            }
        }
    }, [params]);

    useEffect(() => {
        setCurrentHeaderType(headerType);
    }, [headerType]);

    useEffect(() => {
        window.addEventListener(Common.EXTERNAL_EVENT_BACK, onBackPressEvent);
        window.addEventListener(Common.EXTERNAL_EVENT_RESUME, onResumeAppEvent);

        return () => {
            window.removeEventListener(Common.EXTERNAL_EVENT_BACK, onBackPressEvent);
            window.removeEventListener(Common.EXTERNAL_EVENT_RESUME, onResumeAppEvent);
        };
    }, [location]);

    useEffect(() => {
        currentPaymentCategoryId && requestGetPaymentCategory();
    }, [currentPaymentCategoryId]);

    const requestAxiosGetPaymentCategory = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_PAYMENT_CATEGORY_URL + "/" + currentPaymentCategoryId);
        return response.data;
    };

    const {
        loading: loadingGetPaymentCategory,
        error: errorGetPaymentCategory,
        data: resultGetPaymentCategory,
        execute: requestGetPaymentCategory,
    } = useAsyncAxios(requestAxiosGetPaymentCategory);

    useEffect(() => {
        if (resultGetPaymentCategory === null) return;
        console.log("resultGetPaymentCategory", resultGetPaymentCategory);
        setPaymentCategory(resultGetPaymentCategory.rentPaymentCategory);
    }, [resultGetPaymentCategory]);

    useEffect(() => {
        if (errorGetPaymentCategory === null) return;
        console.log("errorGetPaymentCategory", errorGetPaymentCategory);
    }, [errorGetPaymentCategory]);

    useEffect(() => {
        if (paymentCategory === undefined) return;

        const tempHeaderType = { ...currentHeaderType };
        tempHeaderType.title = paymentCategory?.rentPaymentCategoryName;
        setCurrentHeaderType(tempHeaderType);
    }, [paymentCategory]);

    useEffect(() => {
        if (manualContent === undefined) return;

        const tempHeaderType = { ...currentHeaderType };
        tempHeaderType.title = manualContent;
        setCurrentHeaderType(tempHeaderType);
        setManualContent(undefined);
    }, [manualContent]);

    const onResumeAppEvent = async () => {
        console.log("onResumeAppEvent");
    };

    const onBackPressEvent = async () => {
        if (Utils.getModalStack() !== 0) {
            Utils.triggerEvent(Common.EVENT_CLOSE_ALL_MODAL);
            Utils.initModalStack();
        } else if (Utils.getDrawerStack() !== 0) {
            Utils.triggerEvent(Common.EVENT_CLOSE_ALL_DRAWER);
            Utils.initDrawerStack();
        } else {
            const path = location.pathname;
            switch (path) {
                case Common.PAGE_LOGIN:
                case Common.PAGE_MAIN:
                    window.Android.closeAppOnBackPress();
                    break;
                default:
                    navigate(-1);
            }
        }
    };

    return (
        <div className={`${overflow === false ? "overflow-hidden" : ""} ${styles.container}`}>
            <PageHeader headerType={currentHeaderType} />
            {overflow === false ? (
                <div>{children}</div>
            ) : (
                <motion.div variants={pageVariants} initial="squish" animate="appear" exit="exit">
                    {children}
                </motion.div>
            )}

            {showFooter && <PageFooter />}
        </div>
    );
}

export default PageLayout;
